import { useEffect, useState } from "react";
import { getStudentByEmail } from "../../services/StudentService";
import { getStudiesByStudentId } from "../../services/StudyService";
import { SideBar } from "./inc/sideBar";
import { TopHeader } from "./inc/topHeader";
import { Link, useNavigate } from "react-router-dom";
import DataTable from "react-data-table-component";
import HSTSCustomStyle from "../../utils/hstsCustomStyle";
import { useUser } from "../../utils/userProvider";

const MyStudy = () => {
  const { user } = useUser();
  const navigator = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeMenu, setActiveMenu] = useState(null);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024
  );

  const [studies, setStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [perPage, setPerPage] = useState(5);

  const email = user?.email;

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (windowWidth < 768) {
      setSidebarOpen(false);
    }
  }, [windowWidth]);

  const toggleMenu = (menu) => {
    setActiveMenu(activeMenu === menu ? null : menu);
  };

  useEffect(() => {
    const fetchStudentAndStudies = async () => {
      try {
        const studentRes = await getStudentByEmail(email);
        const student = studentRes.data;

        const studyRes = await getStudiesByStudentId(student.studentId);
        setStudies(studyRes.data);
      } catch (err) {
        console.error("Error loading study data:", err);
        setError("Failed to load study data.");
      } finally {
        setLoading(false);
      }
    };

    fetchStudentAndStudies();
  }, [email]);

  const filteredStudies = studies.filter((study) => {
    const search = searchText.toLowerCase();
    return (
      study.level?.toLowerCase().includes(search) ||
      study.univeristyName?.toLowerCase().includes(search) ||
      study.country?.toLowerCase().includes(search) ||
      study.finishDate?.toLowerCase().includes(search)
    );
  });

  const getYearsOfStudy = (startDateStr, finishDateStr) => {
    const startDate = new Date(startDateStr);
    const endDate = new Date(finishDateStr);

    let years = endDate.getFullYear() - startDate.getFullYear();
    let months = endDate.getMonth() - startDate.getMonth();

    if (months < 0) {
      years--;
      months += 12;
    }

    let result = `${years} year${years !== 1 ? "s" : ""}`;
    if (months > 0) {
      result += `, ${months} month${months !== 1 ? "s" : ""}`;
    }

    return result;
  };

  const addProgress = (id) => navigator(`/student/addProgress/${id}`);
  const viewProgress = (id) => navigator(`/student/viewProgress/${id}`);
  const columns = [
    {
      name: "S/N",
      selector: (row, index) => index + 1,
      width: "70px",
    },
    {
      name: "Level",
      selector: (row) => row.level,
      sortable: true,
    },
    {
      name: "University",
      selector: (row) => row.univeristyName,
      sortable: true,
    },
    {
      name: "Country",
      selector: (row) => row.country,
      sortable: true,
    },
    {
      name: "Years of Study",
      selector: (row) => getYearsOfStudy(row.startDate, row.finishDate),
      sortable: false,
    },
    {
      name: "Start Date",
      selector: (row) => row.startDate,
      sortable: true,
    },
    {
      name: "Finish Date",
      selector: (row) => row.finishDate,
      sortable: true,
    },
    {
      name: "Add",
      cell: (row) => (
        <button
          className="btn btn-link text-center zis-ponter"
          title="Add study progress"
          onClick={() => addProgress(row.studyId)}
        >
          <span className="btn btn-secondary rounded-2 fw-bold"> Add</span>
        </button>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "160px",
    },
    {
      name: "View",
      cell: (row) => (
        <button
          className="btn btn-link text-center zis-ponter"
          title="View study progress"
          onClick={() => viewProgress(row.studyId)}
        >
          <span className="btn btn-primary rounded-2 fw-bold">View</span>
        </button>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "160px",
    },
  ];

  return (
    // <div className="wrapper">
    <div className="d-flex position-relative" style={{ minHeight: "100vh" }}>
      <SideBar
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        activeMenu={activeMenu}
        toggleMenu={toggleMenu}
        windowWidth={windowWidth}
      />
      <div className="main-panel">
        <TopHeader
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          windowWidth={windowWidth}
        />
        <div className="container mt-0">
          <div className="page-inner">
            <div className="page-header bg-white zis-shadow rounded-2 mt-2">
              <ul className="breadcrumbs my-md-3 mb-3 ms-0">
                <li className="nav-home">
                  <Link to="/student" className="ps-3 ps-md-0">
                    <span className="fa fa-home"></span>
                  </Link>
                </li>
                <li className="separator">
                  <i className="fas fa-chevron-right text-secondary"></i>
                </li>
                <li className="nav-item text-black fw-semibold">
                  <Link to="#">My study</Link>
                </li>
              </ul>
            </div>

            <div className="card zis-shadow mt-4 rounded-2">
              <div className="card-header d-flex justify-content-between align-items-center mt-3">
                <h4 className="fw-semibold text-black mb-0">
                  My study records
                </h4>
                <div className="d-flex align-items-center">
                  <span className="me-2">Show</span>
                  <select
                    className="form-select form-select-sm"
                    style={{ width: "80px" }}
                    value={perPage}
                    onChange={(e) => setPerPage(Number(e.target.value))}
                  >
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                  </select>
                  <span className="ms-2">entries</span>
                </div>
              </div>

              <div className="card-body">
                <div className="d-flex justify-content-end mb-3">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Search..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    style={{ width: "200px" }}
                  />
                </div>

                <div className="table-responsive">
                  {error ? (
                    <div className="text-danger text-center">{error}</div>
                  ) : (
                    <DataTable
                      columns={columns}
                      data={filteredStudies}
                      progressPending={loading}
                      progressComponent={
                        <div className="text-center py-3">
                          Loading study records...
                        </div>
                      }
                      noDataComponent={
                        <div className="text-center py-3">
                          No study records found
                        </div>
                      }
                      pagination
                      paginationPerPage={perPage}
                      paginationRowsPerPageOptions={[5, 10, 20, 50]}
                      customStyles={HSTSCustomStyle}
                      highlightOnHover
                      responsive
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyStudy;
